import { useNavigate } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { useMyPermissions } from '@/hooks/useMyPermissions'
import { useMe } from '@/hooks/useMe'
import { useViewMode } from '@/stores/viewMode'
import { AllowedAction } from '@/data/allowedActions'
import { Users, Package, UserCog, Megaphone, Monitor, Tag } from 'lucide-react'
import { PageHeader } from '@/components/PageHeader'

interface ToolItem {
  id: string
  name: string
  description: string
  icon: React.ElementType
  /**
   * Path segment after the scope prefix (e.g. 'apps/announcements').
   * Final URL is built in the page based on current mode:
   *   admin  → /admin/apps/announcements
   *   provider → /provider/:provider_id/apps/announcements
   */
  subpath: string
  requiredPermission?: {
    module: string
    action: AllowedAction
  }
}

const tools: ToolItem[] = [
  {
    id: 'create-all-clinic-user-role',
    name: '建立所有診所角色',
    description: '批量為多個診所建立相同的角色並設定權限',
    icon: Users,
    subpath: 'apps/create-all-clinic-user-role',
    requiredPermission: {
      module: 'provider_clinic_management',
      action: AllowedAction.C,
    },
  },
  {
    id: 'provider-clinic-users',
    name: '管理所有診所人員',
    description: '集中檢視並管理 Provider 下所有診所的使用者',
    icon: UserCog,
    subpath: 'apps/provider-clinic-users',
    requiredPermission: {
      module: 'provider_clinic_management',
      action: AllowedAction.R,
    },
  },
  {
    id: 'announcements',
    name: '公告管理',
    description: '建立與管理對旗下診所或廣告商的公告訊息',
    icon: Megaphone,
    subpath: 'apps/announcements',
    requiredPermission: {
      module: 'provider_toolkit',
      action: AllowedAction.R,
    },
  },
  {
    id: 'tv-templates',
    name: '電視模板管理',
    description: '管理電視 VOD 配置模板，批次套用到多台設備',
    icon: Monitor,
    subpath: 'apps/tv-templates',
    requiredPermission: {
      module: 'provider_toolkit',
      action: AllowedAction.R,
    },
  },
  {
    id: 'tag-management',
    name: '標籤管理',
    description: '管理 Provider 旗下診所共用的標籤名稱，重新命名與刪除會同步影響診所',
    icon: Tag,
    subpath: 'apps/tag-management',
    requiredPermission: {
      module: 'provider_clinic_management',
      action: AllowedAction.U,
    },
  },
]

export default function ToolkitPage() {
  const navigate = useNavigate()
  const { hasPermission } = useMyPermissions()
  const { user } = useMe()
  const { mode, id } = useViewMode()

  // sys_admin 可以看到所有工具
  const isSysAdmin = user?.role_type === 'sys_admin'

  const visibleTools = tools.filter((tool) => {
    if (isSysAdmin) return true
    if (!tool.requiredPermission) return true
    return hasPermission(tool.requiredPermission.module, tool.requiredPermission.action)
  })

  // scope 前綴：provider 視角 → /provider/:id，其他 → /admin
  const scopePrefix = mode === 'provider' && id ? `/provider/${id}` : '/admin'

  return (
    <div className="space-y-6">
      <PageHeader
        title="工具包"
        description="批量管理工具，提升營運效率"
        icon={<Package className="h-8 w-8" />}
        level={mode === 'admin' ? 'page' : 'section'}
      />

      {visibleTools.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-muted-foreground">目前沒有可使用的工具</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {visibleTools.map((tool) => {
            const Icon = tool.icon
            return (
              <Card
                key={tool.id}
                className="cursor-pointer hover:border-primary transition-colors"
                onClick={() => navigate(`${scopePrefix}/${tool.subpath}`)}
              >
                <CardHeader className="flex flex-row items-center gap-4">
                  <div className="p-3 rounded-xl bg-primary/10">
                    <Icon className="h-8 w-8 text-primary" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">{tool.name}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-sm">{tool.description}</CardDescription>
                </CardContent>
              </Card>
            )
          })}
        </div>
      )}
    </div>
  )
}
